"use client";

import { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { readSession } from "@/lib/session";

const useClientLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const ADMIN_PATHS = ["/donations", "/my-donations", "/approvals", "/expenses", "/balance-sheet", "/events", "/updates", "/dashboard"];
const SUPER_PATHS = ["/super"];

function matchesPath(pathname: string, prefix: string) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isStaffRole(role?: string) {
  return role === "admin" || role === "superadmin";
}

export function isAdminPath(pathname: string) {
  return ADMIN_PATHS.some((prefix) => matchesPath(pathname, prefix));
}

export function isSuperPath(pathname: string) {
  return SUPER_PATHS.some((prefix) => matchesPath(pathname, prefix));
}

export function isProtectedPath(pathname: string) {
  return (
    isAdminPath(pathname) ||
    isSuperPath(pathname) ||
    matchesPath(pathname, "/profile") ||
    matchesPath(pathname, "/directory") ||
    matchesPath(pathname, "/receipt")
  );
}

export function sessionDecision(pathname: string): { ok: true } | { ok: false; href: string } {
  const session = readSession();
  if (!isProtectedPath(pathname)) return { ok: true };
  if (!session) return { ok: false, href: "/login" };
  if (isSuperPath(pathname) && session.role !== "superadmin") {
    return { ok: false, href: session.role === "admin" ? "/donations" : "/" };
  }
  if (isAdminPath(pathname) && !isStaffRole(session.role)) {
    const memberPage =
      (session.role === "member" || session.role === "committee") &&
      (pathname === "/donations" || pathname === "/events" || pathname.startsWith("/events/"));
    if (memberPage) return { ok: true };
    if (session.role === "committee" && matchesPath(pathname, "/balance-sheet")) return { ok: true };
    return { ok: false, href: "/" };
  }
  return { ok: true };
}

export function enforceSession(pathname: string) {
  const decision = sessionDecision(pathname);
  if (!decision.ok) window.location.replace(decision.href);
  return decision.ok;
}

export function AuthSessionGuard() {
  const pathname = usePathname();

  useClientLayoutEffect(() => {
    enforceSession(pathname);
  }, [pathname]);

  useEffect(() => {
    const checkAuth = () => {
      if (!readSession() && isProtectedPath(window.location.pathname)) {
        document.documentElement.style.visibility = "hidden";
        window.location.replace("/login");
      }
    };

    checkAuth();

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) checkAuth();
    };
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted && isProtectedPath(window.location.pathname) && !readSession()) {
        document.documentElement.style.visibility = "hidden";
      }
    };

    // Validate httpOnly JWT against DB session_token (single active device).
    let cancelled = false;
    async function validateServerSession() {
      if (!readSession() || window.location.pathname === "/login") return;
      try {
        const response = await fetch("http://localhost:5002/api/profile", { credentials: "include" });
        if (cancelled) return;
        if (response.status === 401) {
          // SecurityWrapper shows the "another device" toast + redirect.
          const data = await response.clone().json().catch(() => null);
          if (!String(data?.message || "").toLowerCase().includes("another device") && isProtectedPath(window.location.pathname)) {
            document.cookie = "avt_session=; Path=/; Max-Age=0; SameSite=Strict";
            window.location.replace("/login");
          }
        }
      } catch {
        // Ignore network errors; cookie/JWT checks still apply.
      }
    }
    validateServerSession();

    const onFocus = () => {
      checkAuth();
      validateServerSession();
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("popstate", checkAuth);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("popstate", checkAuth);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [pathname]);

  return null;
}

export default function ProtectedRoute({
  children,
  role,
}: {
  children: ReactNode;
  role?: "admin" | "superadmin";
}) {
  const pathname = usePathname();
  const [allowed, setAllowed] = useState(false);

  useClientLayoutEffect(() => {
    const session = readSession();
    if (!session) {
      setAllowed(false);
      window.location.replace("/login");
      return;
    }
    if (role === "superadmin" && session.role !== "superadmin") {
      setAllowed(false);
      window.location.replace(session.role === "admin" ? "/donations" : "/");
      return;
    }
    if (role === "admin" && !isStaffRole(session.role)) {
      setAllowed(false);
      window.location.replace("/");
      return;
    }
    setAllowed(true);
  }, [pathname, role]);

  useEffect(() => {
    const checkAuth = () => {
      if (!readSession()) {
        document.documentElement.style.visibility = "hidden";
        window.location.replace("/login");
      }
    };

    checkAuth();

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) checkAuth();
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  if (!allowed) return null;
  return children;
}
